import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import './ViewMonthEndSubmissions.css';

function ViewMonthEndSubmissions({ onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      
      // Query without orderBy to avoid requiring a composite index
      const q = query(
        collection(db, 'monthEndSubmissions'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const snapshot = await getDocs(q);
      const submissionsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort client-side by submissionDate (newest first)
      submissionsList.sort((a, b) => {
        const dateA = a.submissionDate?.toDate ? a.submissionDate.toDate() : new Date(a.submissionDate);
        const dateB = b.submissionDate?.toDate ? b.submissionDate.toDate() : new Date(b.submissionDate);
        return dateB - dateA;
      });
      
      setSubmissions(submissionsList);
    } catch (error) {
      console.error('Error loading submissions:', error);
      alert('Error loading month-end submissions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toFixed(2)}`;
  };

  // Generate Month-End Return PDF with student breakdown
  const generateMonthEndReturnPDF = (submission) => {
    const doc = new jsPDF();
    const students = submission.students || [];
    const tt = submission.termTotals || {};
    
    // Sort students by school, then by name
    const sortedStudents = [...students].sort((a, b) => {
      if (a.schoolName !== b.schoolName) {
        return (a.schoolName || '').localeCompare(b.schoolName || '');
      }
      return (a.studentName || '').localeCompare(b.studentName || '');
    });

    // Header
    doc.setFillColor(65, 105, 225);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Month-End Return', 105, 15, { align: 'center' });
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(`${submission.monthName} ${submission.year}`, 105, 25, { align: 'center' });

    // Franchisee info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.text(`Franchisee: ${submission.franchiseeName || 'N/A'}`, 14, 45);
    doc.text(`Submitted: ${formatDate(submission.submissionDate)}`, 14, 52);

    // Student breakdown table
    const tableData = sortedStudents.map(student => {
      const total = (student.term1Paid || 0) + (student.term2Paid || 0) + 
                    (student.term3Paid || 0) + (student.term4Paid || 0);
      return [
        student.schoolName || '',
        student.studentName || '',
        formatCurrency(student.term1Paid),
        formatCurrency(student.term2Paid),
        formatCurrency(student.term3Paid),
        formatCurrency(student.term4Paid),
        formatCurrency(total)
      ];
    });

    // Add totals row
    const totalTerm1 = sortedStudents.reduce((sum, s) => sum + (s.term1Paid || 0), 0);
    const totalTerm2 = sortedStudents.reduce((sum, s) => sum + (s.term2Paid || 0), 0);
    const totalTerm3 = sortedStudents.reduce((sum, s) => sum + (s.term3Paid || 0), 0);
    const totalTerm4 = sortedStudents.reduce((sum, s) => sum + (s.term4Paid || 0), 0);
    const grandTotal = totalTerm1 + totalTerm2 + totalTerm3 + totalTerm4;

    tableData.push([
      'TOTALS',
      '',
      formatCurrency(totalTerm1),
      formatCurrency(totalTerm2),
      formatCurrency(totalTerm3),
      formatCurrency(totalTerm4),
      formatCurrency(grandTotal)
    ]);

    autoTable(doc, {
      startY: 60,
      head: [['School', 'Student', 'Term 1', 'Term 2', 'Term 3', 'Term 4', 'Total']],
      body: tableData,
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3
      },
      headStyles: {
        fillColor: [65, 105, 225],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 45 },
        2: { cellWidth: 22, halign: 'right' },
        3: { cellWidth: 22, halign: 'right' },
        4: { cellWidth: 22, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 22, halign: 'right', fontStyle: 'bold' }
      },
      didParseCell: function(data) {
        // Style the totals row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [240, 240, 240];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Royalty Summary section
    const finalY = doc.lastAutoTable.finalY + 15;
    
    doc.setFillColor(39, 174, 96);
    doc.rect(14, finalY, 182, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Royalty Summary', 105, finalY + 6, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const summaryY = finalY + 15;
    doc.text(`Total Term Fees (YTD):`, 14, summaryY);
    doc.text(formatCurrency(submission.totalYearlyFees), 90, summaryY, { align: 'right' });
    
    doc.text(`Royalty Base (10%):`, 14, summaryY + 7);
    doc.text(formatCurrency(submission.royaltyBase), 90, summaryY + 7, { align: 'right' });
    
    doc.text(`VAT:`, 14, summaryY + 14);
    doc.text(formatCurrency(submission.vat), 90, summaryY + 14, { align: 'right' });
    
    doc.text(`Total Royalty (Year):`, 14, summaryY + 21);
    doc.text(formatCurrency(submission.totalRoyalty), 90, summaryY + 21, { align: 'right' });
    
    doc.text(`Less Previous Payments:`, 14, summaryY + 28);
    doc.text(`- ${formatCurrency(submission.previousRoyaltiesPaid)}`, 90, summaryY + 28, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(39, 174, 96);
    doc.rect(14, summaryY + 33, 80, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(`Amount Due This Month:`, 16, summaryY + 38);
    doc.text(formatCurrency(submission.royaltyAmount), 88, summaryY + 38, { align: 'right' });

    // Summary of Deposits section
    const depositsY = summaryY + 50;
    doc.setTextColor(0, 0, 0);
    doc.setFillColor(65, 105, 225);
    doc.rect(14, depositsY, 182, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.text('Summary of Deposits', 105, depositsY + 6, { align: 'center' });

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    const depY = depositsY + 15;
    doc.text(`Royalty:`, 14, depY);
    doc.text(formatCurrency(submission.royaltyAmount), 90, depY, { align: 'right' });
    
    doc.text(`T-Shirt Orders:`, 14, depY + 7);
    doc.text(formatCurrency(submission.tshirtOrders), 90, depY + 7, { align: 'right' });
    
    doc.text(`Courier Fees:`, 14, depY + 14);
    doc.text(formatCurrency(submission.courierFees), 90, depY + 14, { align: 'right' });
    
    if (submission.otherAmount > 0) {
      doc.text(`Other (${submission.otherDescription || 'N/A'}):`, 14, depY + 21);
      doc.text(formatCurrency(submission.otherAmount), 90, depY + 21, { align: 'right' });
    }
    
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(65, 105, 225);
    const totalRowY = submission.otherAmount > 0 ? depY + 28 : depY + 21;
    doc.rect(14, totalRowY, 80, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(`TOTAL ROYALTY ACCOUNT:`, 16, totalRowY + 5.5);
    doc.text(formatCurrency(submission.royaltyAccountPayment), 88, totalRowY + 5.5, { align: 'right' });

    // BrandBuilding
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    const bbY = totalRowY + 15;
    doc.text(`BrandBuilding (R20s):`, 14, bbY);
    doc.text(formatCurrency(submission.brandBuildingAmount), 90, bbY, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(255, 193, 7);
    doc.rect(14, bbY + 5, 80, 8, 'F');
    doc.setTextColor(0, 0, 0);
    doc.text(`TOTAL BRANDBUILDING:`, 16, bbY + 10.5);
    doc.text(formatCurrency(submission.brandBuildingPayment), 88, bbY + 10.5, { align: 'right' });

    // Bank details
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const bankY = bbY + 25;
    doc.text('Royalty Account: LINDA LLOYD CC T/A BEAUX-ARTS KIDZ KRAFTZ | ABSA 730143311 | Branch: 630510', 14, bankY);
    doc.text('BrandBuilding Account: LINDA LLOYD T/A BRANDBUILDING | ABSA 730153082 | Branch: 630510', 14, bankY + 5);

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated: ${new Date().toLocaleString('en-ZA')}`, 14, 285);
    doc.text('BABS - Beaux-Arts Bookkeeping System', 196, 285, { align: 'right' });

    // Save PDF
    doc.save(`MonthEndReturn_${submission.monthName}_${submission.year}.pdf`);
  };

  const exportSubmissionCSV = (submission) => {
    const students = submission.students || [];
    const tt = submission.termTotals || {};
    
    let csv = `MONTH-END RETURN: ${submission.monthName} ${submission.year}\n`;
    csv += `Submitted: ${formatDate(submission.submissionDate)}\n`;
    csv += `Franchisee: ${submission.franchiseeName}\n\n`;
    
    csv += `School,Student,Term 1,Term 2,Term 3,Term 4,Total\n`;
    
    // Sort by school then student
    const sortedStudents = [...students].sort((a, b) => {
      if (a.schoolName !== b.schoolName) return (a.schoolName || '').localeCompare(b.schoolName || '');
      return (a.studentName || '').localeCompare(b.studentName || '');
    });
    
    sortedStudents.forEach(s => {
      const total = (s.term1Paid || 0) + (s.term2Paid || 0) + (s.term3Paid || 0) + (s.term4Paid || 0);
      csv += `"${s.schoolName || ''}","${s.studentName || ''}",`;
      csv += `${(s.term1Paid || 0).toFixed(2)},${(s.term2Paid || 0).toFixed(2)},`;
      csv += `${(s.term3Paid || 0).toFixed(2)},${(s.term4Paid || 0).toFixed(2)},`;
      csv += `${total.toFixed(2)}\n`;
    });
    
    // Totals
    const t1 = students.reduce((sum, s) => sum + (s.term1Paid || 0), 0);
    const t2 = students.reduce((sum, s) => sum + (s.term2Paid || 0), 0);
    const t3 = students.reduce((sum, s) => sum + (s.term3Paid || 0), 0);
    const t4 = students.reduce((sum, s) => sum + (s.term4Paid || 0), 0);
    csv += `\nTOTALS,,${t1.toFixed(2)},${t2.toFixed(2)},${t3.toFixed(2)},${t4.toFixed(2)},${(t1+t2+t3+t4).toFixed(2)}\n`;
    
    csv += `\n=== ROYALTY SUMMARY ===\n`;
    csv += `Total Term Fees (YTD):,${formatCurrency(submission.totalYearlyFees)}\n`;
    csv += `Royalty Due:,${formatCurrency(submission.royaltyAmount)}\n`;
    csv += `Total Royalty Account:,${formatCurrency(submission.royaltyAccountPayment)}\n`;
    csv += `BrandBuilding:,${formatCurrency(submission.brandBuildingPayment)}\n`;
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `MonthEndReturn_${submission.monthName}_${submission.year}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="view-submissions-container">
        <div className="loading-spinner">Loading submissions...</div>
      </div>
    );
  }

  // Detail View
  if (selectedSubmission) {
    const sub = selectedSubmission;
    const tt = sub.termTotals || {};
    const students = sub.students || [];
    
    // Sort students by school, then name
    const sortedStudents = [...students].sort((a, b) => {
      if (a.schoolName !== b.schoolName) return (a.schoolName || '').localeCompare(b.schoolName || '');
      return (a.studentName || '').localeCompare(b.studentName || '');
    });
    
    // Calculate totals
    const totalTerm1 = students.reduce((sum, s) => sum + (s.term1Paid || 0), 0);
    const totalTerm2 = students.reduce((sum, s) => sum + (s.term2Paid || 0), 0);
    const totalTerm3 = students.reduce((sum, s) => sum + (s.term3Paid || 0), 0);
    const totalTerm4 = students.reduce((sum, s) => sum + (s.term4Paid || 0), 0);
    
    return (
      <div className="view-submissions-container">
        <div className="submissions-header">
          <button onClick={() => setSelectedSubmission(null)} className="back-button">
            ← Back to List
          </button>
          <h1>📋 {sub.monthName} {sub.year} Submission</h1>
        </div>

        <div className="submission-detail">
          {/* Submission Info */}
          <div className="detail-card info-card">
            <div className="info-row">
              <span className="info-label">Submitted:</span>
              <span className="info-value">{formatDate(sub.submissionDate)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Submitted By:</span>
              <span className="info-value">{sub.lockedBy || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className="status-badge locked">🔒 Locked</span>
            </div>
          </div>

          {/* Student Breakdown Table */}
          <div className="detail-card">
            <h2>📊 Student Fees Breakdown (Year to Date)</h2>
            {students.length === 0 ? (
              <div className="no-students-message">
                <p>⚠️ Student breakdown not available for this submission.</p>
                <p className="note">Note: Student data is only stored for submissions made after this feature was added.</p>
              </div>
            ) : (
              <div className="students-table-wrapper">
                <table className="students-breakdown-table">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Student</th>
                      <th className="amount-col">Term 1</th>
                      <th className="amount-col">Term 2</th>
                      <th className="amount-col">Term 3</th>
                      <th className="amount-col">Term 4</th>
                      <th className="amount-col total-col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.map((student, index) => {
                      const total = (student.term1Paid || 0) + (student.term2Paid || 0) + 
                                    (student.term3Paid || 0) + (student.term4Paid || 0);
                      return (
                        <tr key={index}>
                          <td>{student.schoolName || '-'}</td>
                          <td>{student.studentName || '-'}</td>
                          <td className="amount-col">{formatCurrency(student.term1Paid)}</td>
                          <td className="amount-col">{formatCurrency(student.term2Paid)}</td>
                          <td className="amount-col">{formatCurrency(student.term3Paid)}</td>
                          <td className="amount-col">{formatCurrency(student.term4Paid)}</td>
                          <td className="amount-col total-col">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="totals-row">
                      <td colSpan="2"><strong>TOTALS</strong></td>
                      <td className="amount-col"><strong>{formatCurrency(totalTerm1)}</strong></td>
                      <td className="amount-col"><strong>{formatCurrency(totalTerm2)}</strong></td>
                      <td className="amount-col"><strong>{formatCurrency(totalTerm3)}</strong></td>
                      <td className="amount-col"><strong>{formatCurrency(totalTerm4)}</strong></td>
                      <td className="amount-col total-col">
                        <strong>{formatCurrency(totalTerm1 + totalTerm2 + totalTerm3 + totalTerm4)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Royalty Calculation */}
          <div className="detail-card">
            <h2>💰 Royalty Calculation</h2>
            <div className="calculation-grid">
              <div className="calc-row">
                <span>Total Term Fees (Year to Date):</span>
                <span className="amount">{formatCurrency(sub.totalYearlyFees)}</span>
              </div>
              <div className="calc-row">
                <span>Royalty Base (10%):</span>
                <span className="amount">{formatCurrency(sub.royaltyBase)}</span>
              </div>
              <div className="calc-row">
                <span>VAT:</span>
                <span className="amount">{formatCurrency(sub.vat)}</span>
              </div>
              <div className="calc-row">
                <span>Total Royalty (Year):</span>
                <span className="amount">{formatCurrency(sub.totalRoyalty)}</span>
              </div>
              <div className="calc-row">
                <span>Less: Previous Payments:</span>
                <span className="amount negative">- {formatCurrency(sub.previousRoyaltiesPaid)}</span>
              </div>
              <div className="calc-row highlight">
                <span><strong>Amount Due This Month:</strong></span>
                <span className="amount highlight-value">{formatCurrency(sub.royaltyAmount)}</span>
              </div>
            </div>
          </div>

          {/* Summary of Deposits */}
          <div className="detail-card deposits-card">
            <h2>📑 Summary of Deposits</h2>
            
            <div className="deposits-section">
              <h3>Royalty Account (730143311)</h3>
              <div className="deposit-row">
                <span>Royalty:</span>
                <span>{formatCurrency(sub.royaltyAmount)}</span>
              </div>
              <div className="deposit-row">
                <span>T-Shirt Orders:</span>
                <span>{formatCurrency(sub.tshirtOrders)}</span>
              </div>
              <div className="deposit-row">
                <span>Courier Fees:</span>
                <span>{formatCurrency(sub.courierFees)}</span>
              </div>
              {sub.otherAmount > 0 && (
                <div className="deposit-row">
                  <span>Other ({sub.otherDescription || 'N/A'}):</span>
                  <span>{formatCurrency(sub.otherAmount)}</span>
                </div>
              )}
              <div className="deposit-row total-row">
                <span><strong>TOTAL TO ROYALTY ACCOUNT:</strong></span>
                <span className="total-amount">{formatCurrency(sub.royaltyAccountPayment)}</span>
              </div>
            </div>

            <div className="deposits-section brandbuilding">
              <h3>BrandBuilding Account (730153082)</h3>
              <div className="deposit-row">
                <span>BrandBuilding (R20s):</span>
                <span>{formatCurrency(sub.brandBuildingAmount)}</span>
              </div>
              <div className="deposit-row total-row">
                <span><strong>TOTAL TO BRANDBUILDING:</strong></span>
                <span className="total-amount">{formatCurrency(sub.brandBuildingPayment)}</span>
              </div>
            </div>
          </div>

          {/* Student Statistics */}
          <div className="detail-card stats-card">
            <h2>👥 Student Statistics</h2>
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-number">{sub.totalStudents || 0}</span>
                <span className="stat-label">Total Students</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{sub.activeStudents || 0}</span>
                <span className="stat-label">Active Students</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{sub.payingStudents || 0}</span>
                <span className="stat-label">Paying Students</span>
              </div>
            </div>
          </div>

          {/* Export Buttons */}
          <div className="action-buttons">
            <button 
              onClick={() => generateMonthEndReturnPDF(sub)}
              className="export-button pdf-button"
              disabled={students.length === 0}
              title={students.length === 0 ? 'Student data not available for this submission' : 'Download PDF'}
            >
              📄 Download PDF
            </button>
            <button 
              onClick={() => exportSubmissionCSV(sub)}
              className="export-button csv-button"
            >
              📥 Export to CSV
            </button>
          </div>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="view-submissions-container">
      <div className="submissions-header">
        <button onClick={onClose} className="back-button">
          ← Back
        </button>
        <h1>📋 Month-End Submissions</h1>
        <p className="subtitle">View your submitted month-end returns</p>
      </div>

      {submissions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h2>No Submissions Yet</h2>
          <p>You haven't submitted any month-end returns yet.</p>
          <p>Go to Month-End Processing to submit your first return.</p>
        </div>
      ) : (
        <div className="submissions-list">
          {submissions.map(sub => (
            <div 
              key={sub.id} 
              className="submission-card"
              onClick={() => setSelectedSubmission(sub)}
            >
              <div className="submission-card-header">
                <div className="submission-period">
                  <span className="month-name">{sub.monthName}</span>
                  <span className="year">{sub.year}</span>
                </div>
                <span className="status-badge locked">🔒 Submitted</span>
              </div>
              
              <div className="submission-card-body">
                <div className="summary-row">
                  <div className="summary-item">
                    <span className="label">Royalty Due:</span>
                    <span className="value">{formatCurrency(sub.royaltyAmount)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">Total Payment:</span>
                    <span className="value highlight">{formatCurrency(sub.royaltyAccountPayment)}</span>
                  </div>
                  <div className="summary-item">
                    <span className="label">BrandBuilding:</span>
                    <span className="value">{formatCurrency(sub.brandBuildingPayment)}</span>
                  </div>
                </div>
                
                <div className="submission-meta">
                  <span className="submitted-date">
                    Submitted: {formatDate(sub.submissionDate)}
                  </span>
                  <span className="student-count">
                    👥 {sub.payingStudents || 0} paying students
                  </span>
                </div>
              </div>

              <div className="submission-card-footer">
                <span className="view-link">Click to view details →</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ViewMonthEndSubmissions;